<template>
  <div v-if="isGeet" id="login-geetest" class="login-geetest-box">
    <i ref="triggerRef"></i>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    isGeet?: boolean
    product?: string
    state?: number | string
  }>(),
  {
    isGeet: false,
    product: 'popup',
    state: 1,
  },
)

const emit = defineEmits<{
  geetPath: [payload: Record<string, any>]
  geetError: []
  closeGeet: []
}>()

const triggerRef = ref<HTMLElement | null>(null)
const appStore = useAppStore()
const { invokeV3 } = useLoginApi()

const languageMap: Record<string, string> = {
  zh: 'zho-tw',
  en: 'eng',
  'zh-cn': 'zh-cn',
  ja: 'jpn',
  id: 'ind',
  ko: 'kor',
  ru: 'rus',
  ar: 'ara',
  pt: 'por',
  fr: 'fra',
  de: 'deu',
}

let captchaInstance: any = null
let activeOnce = false

const loadGeetestScript = () =>
  new Promise<void>((resolve, reject) => {
    if (!import.meta.client) {
      reject(new Error('client only'))
      return
    }

    if ((window as any).initGeetest4) {
      resolve()
      return
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-login-geetest="true"]')
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('load geetest failed')), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = 'https://static.geetest.com/v4/gt4.js'
    script.async = true
    script.dataset.loginGeetest = 'true'
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', () => reject(new Error('load geetest failed')), { once: true })
    document.head.appendChild(script)
  })

const getGeetestLanguage = () => languageMap[appStore.LanguageCode || ''] || 'en'

const initCaptcha = (captchaId: string) => {
  const initGeetest4 = (window as any).initGeetest4
  if (!initGeetest4) {
    emit('geetError')
    return
  }

  initGeetest4(
    {
      captchaId,
      product: props.product,
      language: getGeetestLanguage(),
    },
    (captcha: any) => {
      captchaInstance = captcha
      captcha.appendTo('#login-geetest')
      captcha.onReady(() => {
        if (props.product === 'bind') {
          captcha.showCaptcha()
        }
        captcha.onClose(() => emit('closeGeet'))
      })
      captcha.onSuccess(() => emit('geetPath', captcha.getValidate()))
      captcha.onError(() => {
        emit('geetError')
        captcha.destroy?.()
        captchaInstance = null
      })
    },
  )
}

const getCaptchaId = async () => {
  if (Number(props.state) === 1) {
    initCaptcha('d70e5a0c2434308c399adb5b3a4650d3')
    return
  }

  const result = await invokeV3('usercenter$getcaptchaid', {
    areaCode: appStore.usergetCountry?.code || '0086',
  }).catch(() => null)
  const captchaId = result?.usercenter?.getcaptchaid?.data
  if (captchaId) {
    initCaptcha(captchaId)
    return
  }

  emit('geetError')
}

watch(
  () => props.isGeet,
  async (value) => {
    if (!value || activeOnce || !import.meta.client) return
    activeOnce = true
    window.setTimeout(() => {
      activeOnce = false
    }, 1500)

    try {
      await loadGeetestScript()
      await getCaptchaId()
      triggerRef.value?.click()
    } catch {
      emit('geetError')
    }
  },
)

onBeforeUnmount(() => {
  captchaInstance?.destroy?.()
})
</script>

<style>
.geetest_captcha,
.geetest_holder,
.geetest_wind,
.geetest_detect,
.geetest_holder.geetest_silver .geetest_widget {
  direction: ltr;
}

.geetest_progress {
  width: auto !important;
  height: auto !important;
  line-height: normal !important;
}

.login-geetest-box {
  margin-top: 10px;
}

.geetest_popup_box {
  border-radius: 10px !important;
}

.geetest_holder.geetest_wind .geetest_radar_btn,
.geetest_holder.geetest_wind .geetest_success_btn {
  border-color: #ecedf0;
}

.geetest_holder,
.geetest_wind,
.geetest_detect {
  width: 100% !important;
}
</style>
